import { Injectable, inject } from "@angular/core";
import { ApiSchemas, BackendApiService, apiErrorMessage } from "@app/common/services/backend-api.service";

type DirectoryEnvelope = ApiSchemas["DirectoryEnvelope"];
import { Observable, catchError, map, of, from } from "rxjs";
import dayjs from "dayjs/esm";
import type { Portfolio } from "./portfolio-data.service";
import {
  calculatePortfolioBalanceRangeRoi,
  derivePortfolioBalanceSeries,
} from "../components/portfolio-ai/portfolio-balance";
import {
  metricDayPl,
  type PortfolioBalanceAnchor,
  type PortfolioBalancePoint,
} from "../components/portfolio-ai/types";

/** Kept for callers/tests that import it; the chart history is no longer paged client-side. */
export const VALUATION_PAGE_SIZE = 5000;

interface PublicPortfolioUserRow {
  id: string;
  username: string;
}

interface PublicPortfolioRow {
  id: number;
  name: string;
  description?: string | null;
  userId: string;
  brokerId: number;
  createdAt: string;
  updatedAt: string;
  fingerPrint?: string | null;
  isPublic: boolean;
  broker?: {
    id: number;
    name: string;
    createdAt?: string | null;
  } | null;
  tradeHistoriesAggregate?: {
    _count: number;
  } | null;
}

/**
 * A user-entered Net Liquidating Value anchor. These are sentinel
 * `_BALANCE_ENTRY` rows in `open_trades` (see the balances store in
 * cloud-run/tradeparse-backend/src/api/balances.js)
 * — the manual, optional balance a trader records so NLV can be tracked
 * without a broker integration.
 */
interface PublicPortfolioBalanceRow {
  portfolioId: number;
  startingBalance: number | string | null;
  startingBalanceDate: string | null;
  description?: string | null;
}

interface PublicPortfolioValuationRow {
  portfolioId: number;
  asOfDate: string;
  marketValue: number | string;
  totalPl: number | string;
  realizedPlCumulative: number | string;
  unrealizedPl: number | string;
  positionCount: number | string;
  metadata?: Record<string, unknown> | null;
}

/**
 * A pre-rendered sparkline for a metric series, sized to a 100 x 32 viewBox so
 * the template can bind it directly without per-change-detection math. `line` is
 * the polyline points; `area` closes the same path down to the baseline for a
 * gradient fill. Built once per portfolio at load time.
 */
export interface PortfolioSparkLine {
  line: string;
  area: string;
}

export interface PublicPortfolioSummary {
  id: number;
  name: string;
  description?: string | null;
  userId: string;
  ownerUsername: string;
  brokerId: number;
  brokerName?: string | null;
  createdAt: string;
  updatedAt: string;
  isPublic: boolean;
  isVerified?: boolean;
  tradeCount: number;
  firstAsOfDate?: string | null;
  latestAsOfDate?: string | null;
  marketValue?: number | null;
  totalPl?: number | null;
  realizedPlCumulative?: number | null;
  unrealizedPl?: number | null;
  positionCount?: number | null;
  todayPl?: number | null;
  // Net Liquidating Value on the latest reconstructed day, derived from the
  // trader's manually entered balance anchors. Null when the portfolio has no
  // anchor (the feature is optional) or none resolves to a reconstructed day.
  netLiq?: number | null;
  // P/L-implied annualized return over the portfolio's full reconstructed
  // history. Percentage points (29.5 means 29.5%).
  cagrPct?: number | null;
  trackedDays?: number;
  valueSpark?: PortfolioSparkLine | null;
  totalPlSpark?: PortfolioSparkLine | null;
  realizedSpark?: PortfolioSparkLine | null;
  /** Server-issued capability for the current viewer (global admin or owner). */
  viewerCanWrite?: boolean;
  /** Firebase uid the ephemeral viewer capability was issued to. */
  viewerCanWriteForUserId?: string;
}

export type PublicPortfolioEntity = Portfolio & {
  ownerUsername: string;
  viewerCanWrite?: boolean;
  viewerCanWriteForUserId?: string;
};

export type PublicPortfolioDirectorySortField =
  | "recent"
  | "portfolio"
  | "verified"
  | "user"
  | "info"
  | "broker"
  | "asof"
  | "netliq"
  | "cagr"
  | "todaypl"
  | "totalpl"
  | "openpl"
  | "realizedpl"
  | "trades"
  | "days";

export interface PublicPortfolioDirectoryPageRequest {
  scope: "owned" | "public";
  userId?: string | null;
  page: number;
  search: string;
  verifiedOnly: boolean;
  includeEmpty: boolean;
  sortField: PublicPortfolioDirectorySortField;
  sortDirection: "asc" | "desc";
}

export interface PublicPortfolioDirectoryMetadata {
  page: number;
  pageSize: 50;
  totalItems: number;
  totalPages: number;
  returnedRows: number;
  emptyPortfolioCount: number;
}

export interface PublicPortfolioDirectoryPage {
  items: PublicPortfolioSummary[];
  metadata: PublicPortfolioDirectoryMetadata;
}

export interface PublicPortfolioPlPoint {
  asOfDate: string;
  totalPl: number;
  profitLossChange: number | null;
}

export interface PublicPortfolioChartSeries {
  portfolioId: number;
  name: string;
  ownerUsername: string;
  anchors: PortfolioBalanceAnchor[];
  nlvPoints: PortfolioBalancePoint[];
  nlvRoi: number | null;
  totalPlPoints: PublicPortfolioPlPoint[];
}

@Injectable({
  providedIn: "root",
})
export class PublicPortfolioService {
  private readonly api = inject(BackendApiService);

  /** POST /portfolio-directory/page. Owned scope is keyed by the caller's token. */
  getDirectoryPage(
    request: PublicPortfolioDirectoryPageRequest
  ): Observable<PublicPortfolioDirectoryPage> {
    return from(
      this.api.client.POST("/portfolio-directory/page", {
        body: {
          scope: request.scope,
          page: request.page,
          search: request.search,
          verifiedOnly: request.verifiedOnly,
          includeEmpty: request.includeEmpty,
          sortField: request.sortField,
          sortDirection: request.sortDirection,
        },
      })
    ).pipe(
      map(({ data, error }) => {
        const result = (data ?? error) as DirectoryEnvelope | undefined;
        if (!result?.success || !result.portfoliosJson || !result.metadataJson) {
          throw new Error(result?.message || apiErrorMessage(error, "Portfolio directory load failed."));
        }
        const items = JSON.parse(result.portfoliosJson);
        const metadata = JSON.parse(result.metadataJson);
        if (
          !Array.isArray(items) ||
          !metadata ||
          typeof metadata !== "object" ||
          metadata.pageSize !== 50
        ) {
          throw new Error("Portfolio directory returned an invalid payload.");
        }
        return { items, metadata } as PublicPortfolioDirectoryPage;
      })
    );
  }

  /** POST /portfolio-directory/refresh-ids for the signed-in owner (the uid argument is no longer sent). */
  getOwnedRefreshPortfolioIds(userId: string): Observable<number[]> {
    void userId;
    return from(this.api.client.POST("/portfolio-directory/refresh-ids", { body: {} })).pipe(
      map(({ data, error }) => {
        const result = (data ?? error) as DirectoryEnvelope | undefined;
        if (!result?.success || !result.portfolioIdsJson) {
          throw new Error(result?.message || apiErrorMessage(error, "Unable to find portfolios to refresh."));
        }
        const ids = JSON.parse(result.portfolioIdsJson);
        if (!Array.isArray(ids) || ids.some((id) => !Number.isInteger(id))) {
          throw new Error("Portfolio refresh lookup returned an invalid payload.");
        }
        return ids;
      })
    );
  }

  /**
   * Full daily history for the directory's multi-portfolio chart. This stays
   * lazy so the normal card view does not download every portfolio's complete
   * reconstruction. Pagination avoids silently ranking only the first 5,000
   * rows when the public directory contains many portfolios.
   */
  getPortfolioChartSeries(
    portfolios: readonly PublicPortfolioSummary[]
  ): Observable<PublicPortfolioChartSeries[]> {
    const portfolioIds = [...new Set(portfolios.map((portfolio) => portfolio.id))];
    if (portfolioIds.length === 0) return of([]);

    // One POST replaces the paged valuation query + anchors query: the server
    // returns full daily history for every readable id (unreadable ids drop out).
    return from(
      this.api.client.POST("/api/v1/public/portfolios/chart-series", { body: { portfolioIds } })
    ).pipe(
      map(({ data, error }) => {
        if (error || !data) throw new Error(apiErrorMessage(error, "Portfolio chart history load failed."));
        return {
          rows: data.valuations as PublicPortfolioValuationRow[],
          anchorsByPortfolio: this.buildAnchorLookup(data.anchors as PublicPortfolioBalanceRow[]),
        };
      }),
      map(({ rows, anchorsByPortfolio }) => {
        const rowsByPortfolio = new Map<number, PublicPortfolioValuationRow[]>();
        for (const row of rows) {
          const bucket = rowsByPortfolio.get(row.portfolioId) ?? [];
          bucket.push(row);
          rowsByPortfolio.set(row.portfolioId, bucket);
        }

        return portfolios.map((portfolio) => {
          const portfolioRows = (rowsByPortfolio.get(portfolio.id) ?? []).sort(
            (a, b) => a.asOfDate.localeCompare(b.asOfDate)
          );
          const anchors = anchorsByPortfolio.get(portfolio.id) ?? [];
          const balanceRows = portfolioRows.map((row) => ({
            asOfDate: row.asOfDate,
            realizedPlCumulative:
              this.toNumber(row.realizedPlCumulative) ?? 0,
            unrealizedPl: this.toNumber(row.unrealizedPl) ?? 0,
            metadata: row.metadata ?? null,
          }));
          const nlvPoints =
            anchors.length > 0
              ? derivePortfolioBalanceSeries(balanceRows, anchors)
              : [];
          const nlvRoi = calculatePortfolioBalanceRangeRoi(nlvPoints)?.returnPct ?? null;

          let previousTotalPl: number | null = null;
          const totalPlPoints: PublicPortfolioPlPoint[] = [];
          for (const row of portfolioRows) {
            const totalPl = this.toNumber(row.totalPl);
            if (totalPl == null) continue;
            const fallback =
              previousTotalPl == null ? null : totalPl - previousTotalPl;
            totalPlPoints.push({
              asOfDate: row.asOfDate,
              totalPl,
              profitLossChange: metricDayPl(row, fallback),
            });
            previousTotalPl = totalPl;
          }

          return {
            portfolioId: portfolio.id,
            name: portfolio.name,
            ownerUsername: portfolio.ownerUsername,
            anchors,
            nlvPoints,
            nlvRoi,
            totalPlPoints,
          };
        });
      })
    );
  }

  getPublicPortfolioById(
    portfolioId: number
  ): Observable<PublicPortfolioSummary | null> {
    return this.getPortfolioForViewer(portfolioId, null);
  }

  /** PATCH /api/v1/portfolios/:id { isPublic: false } — the server enforces ownership. */
  makePortfolioPrivate(
    portfolioId: number,
    ownerUserId: string
  ): Observable<boolean> {
    return from(
      this.api.client.PATCH("/api/v1/portfolios/{id}", {
        params: { path: { id: portfolioId } },
        body: { isPublic: false },
      })
    ).pipe(
      map(({ data, error }) => {
        if (error || !data || data.userId !== ownerUserId) {
          throw new Error(apiErrorMessage(error, "Portfolio was not updated"));
        }
        return data.isPublic === false;
      })
    );
  }

  /**
   * GET /api/v1/public/portfolios/:id. The SERVER decides visibility now
   * (public, owner, or the admin account); a portfolio the caller may not see
   * is a 404 and resolves to null. The viewer/elevated arguments remain for
   * callers' signatures but no longer drive the decision.
   */
  getPortfolioForViewer(
    portfolioId: number,
    viewerUserId: string | null,
    elevatedAccess = false
  ): Observable<PublicPortfolioSummary | null> {
    void viewerUserId;
    void elevatedAccess;
    return from(
      this.api.client.GET("/api/v1/public/portfolios/{id}", { params: { path: { id: portfolioId } } })
    ).pipe(
      map(({ data, error, response }) => {
        if (response?.status === 404 || !data) {
          if (error && response?.status !== 404) {
            throw new Error(apiErrorMessage(error, "Public portfolio load failed."));
          }
          return null;
        }
        const portfolio = data.portfolio;
        const row: PublicPortfolioRow = {
          id: portfolio.id,
          name: portfolio.name,
          description: portfolio.description ?? null,
          userId: portfolio.userId,
          brokerId: portfolio.brokerId,
          createdAt: portfolio.createdAt,
          updatedAt: portfolio.updatedAt,
          fingerPrint: portfolio.fingerPrint ?? null,
          isPublic: portfolio.isPublic,
          broker: portfolio.broker ?? null,
          tradeHistoriesAggregate: { _count: portfolio.tradeCount },
        };
        const anchors = this.buildAnchorLookup(data.anchors as PublicPortfolioBalanceRow[]).get(portfolio.id) ?? [];
        return this.mapSummary(
          row,
          data.valuations as PublicPortfolioValuationRow[],
          new Map([[portfolio.userId, portfolio.ownerUsername]]),
          anchors
        );
      }),
      catchError((error) => {
        console.error("Error loading public portfolio:", error);
        return of(null);
      })
    );
  }

  toPortfolioEntity(summary: PublicPortfolioSummary): PublicPortfolioEntity {
    return {
      id: summary.id,
      name: summary.name,
      ownerUsername: summary.ownerUsername,
      description: summary.description ?? null,
      userId: summary.userId,
      brokerId: summary.brokerId,
      createdAt: summary.createdAt,
      updatedAt: summary.updatedAt,
      fingerPrint: null,
      isPublic: summary.isPublic,
      isVerified: summary.isVerified ?? false,
      tradeCount: summary.tradeCount,
      portfolioTypeId: 2,
      schwabAccountNumber: null,
      broker: summary.brokerName
        ? ({
            id: summary.brokerId,
            name: summary.brokerName,
            createdAt: summary.createdAt,
          } as any)
        : null,
      tradeHistoriesAggregate: {
        _count: summary.tradeCount,
      } as any,
      viewerCanWrite: summary.viewerCanWrite,
      viewerCanWriteForUserId: summary.viewerCanWriteForUserId,
    } as PublicPortfolioEntity;
  }

  buildPortfolioQueryParams(summary: PublicPortfolioSummary): {
    pid: number;
    start: string;
    end: string;
    asof: string;
  } {
    const latest = summary.latestAsOfDate ?? this.toIsoDate(new Date());
    const start = this.maxIsoDate(
      this.addDays(latest, -2),
      summary.firstAsOfDate ?? null
    );

    return {
      pid: summary.id,
      start,
      end: latest,
      asof: latest,
    };
  }

  private mapSummary(
    portfolio: PublicPortfolioRow,
    valuationRows: PublicPortfolioValuationRow[],
    ownerLookup: Map<string, string>,
    balanceAnchors: PortfolioBalanceAnchor[] = []
  ): PublicPortfolioSummary {
    const matchingRows = valuationRows
      .filter((row) => row.portfolioId === portfolio.id)
      .sort((a, b) => b.asOfDate.localeCompare(a.asOfDate));
    const latest = matchingRows[0] ?? null;
    const prior = matchingRows[1] ?? null;
    const first = matchingRows.at(-1) ?? null;

    // "Today P/L" = the most recent reconstructed day's change. Prefer the
    // backend's economic Day P/L (metadata.dayPl), which rebases open positions
    // to the prior-day close/last basis — this is the broker-style figure the
    // Portfolio AI tab shows. Fall back to the raw cumulative-totalPl diff
    // (== metadata.rawDayPl) only when dayPl is absent. Matches metricDayPl().
    const rawTodayPl =
      latest && prior
        ? (this.toNumber(latest.totalPl) ?? 0) - (this.toNumber(prior.totalPl) ?? 0)
        : null;
    const metadataDayPl = this.toNumber(
      (latest?.metadata as Record<string, unknown> | null | undefined)?.["dayPl"] as
        | number
        | string
        | null
        | undefined
    );
    const todayPl = metadataDayPl ?? rawTodayPl;

    // Ascending series over the reconstruction range, used to render the tile
    // sparklines. Sampled down inside the builders so a long history still
    // produces a lightweight, smooth line.
    const ascending = [...matchingRows].reverse();

    return {
      id: portfolio.id,
      name: portfolio.name,
      description: portfolio.description ?? null,
      userId: portfolio.userId,
      ownerUsername: ownerLookup.get(portfolio.userId) ?? "Trader",
      brokerId: portfolio.brokerId,
      brokerName: portfolio.broker?.name ?? null,
      createdAt: portfolio.createdAt,
      updatedAt: portfolio.updatedAt,
      isPublic: portfolio.isPublic,
      tradeCount: Number(portfolio.tradeHistoriesAggregate?._count ?? 0),
      firstAsOfDate: first?.asOfDate ?? null,
      latestAsOfDate: latest?.asOfDate ?? null,
      marketValue: this.toNumber(latest?.marketValue),
      totalPl: this.toNumber(latest?.totalPl),
      realizedPlCumulative: this.toNumber(latest?.realizedPlCumulative),
      unrealizedPl: this.toNumber(latest?.unrealizedPl),
      positionCount: this.toNumber(latest?.positionCount),
      todayPl,
      netLiq: this.deriveLatestNetLiq(ascending, balanceAnchors),
      trackedDays: ascending.length,
      valueSpark: this.buildSparkLine(
        ascending.map((row) => this.toNumber(row.marketValue))
      ),
      totalPlSpark: this.buildSparkLine(
        ascending.map((row) => this.toNumber(row.totalPl))
      ),
      realizedSpark: this.buildSparkLine(
        ascending.map((row) => this.toNumber(row.realizedPlCumulative))
      ),
    };
  }

  /**
   * Render a metric series into an SVG polyline (and matching area polygon)
   * within a 100 x 32 viewBox. Null/non-finite points are dropped, the series
   * is sampled down to at most `maxPoints`, then min/max-normalized so the line
   * fills the box vertically regardless of the metric's absolute scale.
   */
  private buildSparkLine(
    rawValues: Array<number | null>,
    maxPoints = 48
  ): PortfolioSparkLine | null {
    const values = rawValues.filter(
      (value): value is number => value !== null && Number.isFinite(value)
    );
    if (values.length < 2) {
      return null;
    }

    const sampled = this.downsample(values, maxPoints);
    const width = 100;
    const height = 32;
    const padY = 3;
    const min = Math.min(...sampled);
    const max = Math.max(...sampled);
    const span = max - min || 1;
    const count = sampled.length;

    const coords = sampled.map((value, index) => {
      const x = (index / (count - 1)) * width;
      const y = padY + (1 - (value - min) / span) * (height - padY * 2);
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    });

    return {
      line: coords.join(" "),
      area: `0,${height} ${coords.join(" ")} ${width},${height}`,
    };
  }

  /**
   * Evenly sample a series down to at most `maxPoints`, always keeping the
   * first and last points so the sparkline endpoints stay anchored.
   */
  private downsample(values: number[], maxPoints: number): number[] {
    if (values.length <= maxPoints) {
      return values;
    }

    const result: number[] = [];
    const step = (values.length - 1) / (maxPoints - 1);
    for (let i = 0; i < maxPoints; i++) {
      result.push(values[Math.round(i * step)]);
    }
    return result;
  }

  /**
   * Group the `_BALANCE_ENTRY` rows by portfolio, normalized into the same
   * anchor shape the Portfolio AI tab uses so both surfaces run the identical
   * NLV derivation.
   */
  private buildAnchorLookup(
    rows: PublicPortfolioBalanceRow[]
  ): Map<number, PortfolioBalanceAnchor[]> {
    const byPortfolio = new Map<number, PortfolioBalanceAnchor[]>();
    for (const row of rows) {
      const startingBalance = this.toNumber(row.startingBalance);
      if (startingBalance === null || !row.startingBalanceDate) continue;
      const portfolioId = Number(row.portfolioId);
      if (!Number.isFinite(portfolioId)) continue;
      const anchors = byPortfolio.get(portfolioId) ?? [];
      anchors.push({
        id: null,
        startingBalance,
        startingBalanceDate: dayjs(row.startingBalanceDate).format("YYYY-MM-DD"),
        createdAt: null,
        balanceSource: String(row.description || "").startsWith("Schwab NetLiq")
          ? "schwab"
          : "manual",
      });
      byPortfolio.set(portfolioId, anchors);
    }
    return byPortfolio;
  }

  /**
   * NLV on the latest reconstructed day, using the shared derivation
   * (`derivePortfolioBalanceSeries`) so the directory tile agrees with the
   * Portfolio AI NLV chart. Null when the trader entered no balance — the
   * feature is optional and the tile simply hides.
   */
  private deriveLatestNetLiq(
    ascendingRows: PublicPortfolioValuationRow[],
    anchors: PortfolioBalanceAnchor[]
  ): number | null {
    if (anchors.length === 0 || ascendingRows.length === 0) return null;
    const points = derivePortfolioBalanceSeries(
      ascendingRows.map((row) => ({
        asOfDate: row.asOfDate,
        realizedPlCumulative: this.toNumber(row.realizedPlCumulative) ?? 0,
        unrealizedPl: this.toNumber(row.unrealizedPl) ?? 0,
      })),
      anchors
    );
    const latest = points.at(-1);
    return latest && Number.isFinite(latest.netLiquidatingValue)
      ? latest.netLiquidatingValue
      : null;
  }

  private buildOwnerLookup(rows: PublicPortfolioUserRow[]): Map<string, string> {
    return new Map(
      rows.map((user) => [user.id, user.username?.trim() || "Trader"])
    );
  }

  private toNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private addDays(date: string, days: number): string {
    const parsed = new Date(`${date}T00:00:00.000Z`);
    parsed.setUTCDate(parsed.getUTCDate() + days);
    return this.toIsoDate(parsed);
  }

  private maxIsoDate(left: string, right: string | null): string {
    if (!right) {
      return left;
    }

    return left.localeCompare(right) >= 0 ? left : right;
  }

  private toIsoDate(date: Date): string {
    return date.toISOString().slice(0, 10);
  }
}
